import type { Version3Models } from 'jira.js';
import {
  JiraFieldMappers,
  type TemplateMappingSchemaV2,
  type JiraFieldMapper,
  type UIFieldInputType,
  type UiFieldValidationError
} from './mappers';
import { type Template } from '../types';

export interface AllowedValue {
  id: string;
  name: string;
  value: string;
  self?: string;
  description?: string;
}

export interface DefaultValueOption {
  self: string;
  value: string;
  id: string;
  disabled: boolean;
}

export type DefaultValueType =
  | DefaultValueOption
  | DefaultValueOption[]
  | string
  | string[]
  | number;

export interface IssueTypeField {
  allowedValues?: AllowedValue[];
  autoCompleteUrl?: string;
  configuration: object;
  defaultValue?: DefaultValueType;
  fieldId: string;
  hasDefaultValue?: boolean;
  key: string;
  name: string;
  operations: string[];
  required: boolean;
  schema: Version3Models.JsonType;
}

export type UIAllowedValue = {
  id: string;
  name: string;
};

export type UIFieldDefinition = {
  key: string;
  name: string;

  uiFieldType: UIFieldInputType;
  value?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  integrationValue?: any;
  error?: UiFieldValidationError;
  allowedValues?: UIAllowedValue[];

  mapper: JiraFieldMapper;
  rawSchema: IssueTypeField;
};

export type UIFields = {
  requiredFields: UIFieldDefinition[];
  nonRequiredFields: UIFieldDefinition[];
  hasNotEditableRequiredFields: boolean;
};

// these fields are processed on server side, as required special handling
const excludedFromDisplay = ['description'];

export const prepareUIFields = (
  template: Template,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  axeIssue: any
): UIFields => {
  const mappers = Object.values(JiraFieldMappers);

  const v2Mapping =
    template.fieldMappings as unknown as TemplateMappingSchemaV2;

  const uiFields: UIFields = {
    requiredFields: [],
    nonRequiredFields: [],
    hasNotEditableRequiredFields: false
  };
  for (const integrationField of v2Mapping.fields) {
    // Jira Data Center field does not have a key, so we use the fieldId
    const fieldKey = integrationField.key || integrationField.fieldId;
    if (excludedFromDisplay.includes(fieldKey)) {
      continue;
    }

    // Look for mapping for this field
    const fieldMapping = v2Mapping.mapping[fieldKey];

    let mapper: JiraFieldMapper | undefined = undefined;
    // First we will try to get mapper from the mapping definition
    if (fieldMapping) {
      mapper = JiraFieldMappers[fieldMapping.mapperId];
    }

    // If we still do not have a mapper, try to determine which one we can use
    if (!mapper) {
      mapper = mappers.find(mapperToCheck =>
        mapperToCheck.canMapThisField(integrationField)
      );
    }

    // At this point we must have a mapper in order to be able to show this field
    if (mapper) {
      const { value, uiFieldType, allowedValues } = mapper.getDisplayValue(
        integrationField,
        axeIssue,
        fieldMapping
      );

      const uiFieldDefinition: UIFieldDefinition = {
        key: fieldKey,
        name: integrationField.name,
        uiFieldType,
        value,
        mapper,
        rawSchema: integrationField,
        allowedValues
      };

      if (integrationField.required) {
        uiFields.requiredFields.push(uiFieldDefinition);
      } else {
        uiFields.nonRequiredFields.push(uiFieldDefinition);
      }
    } else if (integrationField.required) {
      uiFields.hasNotEditableRequiredFields = true;
    }
  }

  return uiFields;
};

export const prepareUIFieldsForSubmission = (
  uiFields: UIFields
): Record<string, number> => {
  const fieldData: Record<string, number> = {};
  for (const requiredField of uiFields.requiredFields) {
    if (requiredField.integrationValue) {
      fieldData[requiredField.key] = requiredField.integrationValue;
    }
  }
  for (const nonRequiredField of uiFields.nonRequiredFields) {
    if (nonRequiredField.integrationValue) {
      fieldData[nonRequiredField.key] = nonRequiredField.integrationValue;
    }
  }

  return fieldData;
};
